import React, { useState, useEffect } from "react";
import { Users } from "lucide-react";
import { ProjectService } from "../services/projectService";
import { formatDate } from "../utils/dateFormatting";
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button } from "./ui";

const ClientModal = ({
  client,
  isOpen,
  onClose,
  onSave,
  theme = 'light',
}) => {
  const [formData, setFormData] = useState({
    name: "",
    billingStatus: "",
    yearlyCost: "",
    clientsInvoicedDate: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || "",
        billingStatus: client.billing_status || "",
        yearlyCost: client.yearly_cost || "",
        clientsInvoicedDate: client.clients_invoiced_date
          ? formatDate(client.clients_invoiced_date, "input")
          : "",
      });
    }
  }, [client]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const fieldsToUpdate = {
        name: formData.name || null,
        billing_status: formData.billingStatus || null,
        yearly_cost: formData.yearlyCost ? parseFloat(formData.yearlyCost) : null,
        clients_invoiced_date: formData.clientsInvoicedDate || null,
      };

      for (const [field, value] of Object.entries(fieldsToUpdate)) {
        const currentFieldName = field.replace(/_([a-z])/g, (_, letter) =>
          letter.toUpperCase(),
        );
        const clientFieldName = currentFieldName === "clientsInvoicedDate"
          ? "clients_invoiced_date"
          : currentFieldName === "billingStatus"
          ? "billing_status"
          : currentFieldName === "yearlyCost"
          ? "yearly_cost"
          : currentFieldName;

        if (value !== client[clientFieldName]) {
          await ProjectService.updateClient(client.id, field, value);
        }
      }

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error saving client:", err);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!client) return null;

  // Theme-aware styles
  const headerStyles = {
    padding: 'var(--space-6)',
    borderBottom: theme === 'light' ? '1px solid #e5e7eb' : '1px solid var(--gray-700)',
    background: theme === 'light'
      ? 'linear-gradient(to bottom, #f9fafb, #ffffff)'
      : 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
    borderTopLeftRadius: 'var(--radius-xl)',
    borderTopRightRadius: 'var(--radius-xl)',
  };

  const titleColor = theme === 'light' ? '#111827' : 'var(--gray-50)';
  const subtitleColor = theme === 'light' ? '#6b7280' : 'var(--gray-400)';

  return (
    <Modal trackAs="ClientModal" isOpen={isOpen} onClose={onClose} size="md" theme={theme}>
      {/* Header with Icon */}
      <div style={headerStyles}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Users size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: titleColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {client.name || 'Client Details'}
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                color: subtitleColor,
              }}
            >
              Remote Service Client
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {error && (
          <div style={{
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
            background: 'var(--danger-bg, #dc26261a)',
            border: '1px solid var(--danger, #dc2626)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--danger, #dc2626)',
            fontSize: 'var(--text-sm)',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
            color: theme === 'light' ? '#374151' : 'var(--gray-200)',
            paddingBottom: 'var(--space-2)',
            borderBottom: theme === 'light' ? '1px solid #e5e7eb' : '1px solid var(--gray-700)',
          }}>
            Client Information
          </h2>

          <Input
            label="Client Name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter client name"
            required
          />

          <Dropdown
            label="Billing Status"
            value={formData.billingStatus}
            onChange={(value) => handleInputChange("billingStatus", value)}
            options={[
              { value: "", label: "Not Set" },
              { value: "Quoted", label: "Quoted" },
              { value: "Invoiced", label: "Invoiced" },
              { value: "Paid", label: "Paid" },
              { value: "Voided", label: "Voided" },
            ]}
          />

          <Input
            label="Yearly Cost"
            type="number"
            value={formData.yearlyCost}
            onChange={(e) => handleInputChange("yearlyCost", e.target.value)}
            placeholder="Enter yearly cost"
            step="0.01"
          />

          <Input
            label="Client Invoiced Date"
            type="date"
            value={formData.clientsInvoicedDate}
            onChange={(e) => handleInputChange("clientsInvoicedDate", e.target.value)}
          />
        </div>
      </ModalBody>

      <ModalFooter>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          gap: 'var(--space-3)',
        }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ClientModal;
